import { useEffect, useState } from 'react'

export type TargetRectStatus = 'searching' | 'found' | 'missing'

export interface TargetRectState {
  rect: DOMRect | null
  status: TargetRectStatus
  /** Which targetId this status/rect belongs to — consumers MUST check it
   *  before acting on `status` (state updates lag one render behind a
   *  targetId change, so a stale 'missing' from the previous step would
   *  otherwise skip the new one). */
  targetId: string
}

const POLL_INTERVAL_MS = 200
const FIND_TIMEOUT_MS = 5_000

/**
 * Locate a `[data-tour-id]` element and track its viewport rect.
 *
 * - Polls for the element (it may render late — route transition, data
 *   fetch) up to a timeout; `missing` lets the tour skip dead steps.
 * - Tracks the rect through window resize, any scroll (capture phase),
 *   and target resizes — coalesced to one update per animation frame so
 *   inertial scrolling doesn't trigger hundreds of layout reads/commits.
 * - Re-acquires the element when it detaches (e.g. a conditional branch
 *   swaps the button out mid-tour) instead of freezing on a 0×0 rect.
 */
export function useTargetRect(
  targetId: string,
  options: { pollIntervalMs?: number; timeoutMs?: number } = {}
): TargetRectState {
  const pollIntervalMs = options.pollIntervalMs ?? POLL_INTERVAL_MS
  const timeoutMs = options.timeoutMs ?? FIND_TIMEOUT_MS
  const [state, setState] = useState<TargetRectState>(() => ({
    rect: null,
    status: targetId ? 'searching' : 'missing',
    targetId,
  }))

  useEffect(() => {
    if (!targetId) {
      setState({ rect: null, status: 'missing', targetId })
      return
    }
    setState({ rect: null, status: 'searching', targetId })

    let element: Element | null = null
    let resizeObserver: ResizeObserver | null = null
    let pollTimer: ReturnType<typeof setInterval> | null = null
    let frameHandle: number | null = null
    let cancelled = false
    let deadline = Date.now() + timeoutMs

    const find = (): Element | null => document.querySelector(`[data-tour-id="${targetId}"]`)

    const measure = (): void => {
      frameHandle = null
      if (cancelled || !element) return
      if (!element.isConnected) {
        // Target unmounted (conditional render swap) — drop it and go
        // back to polling rather than tracking a detached node's 0×0 rect.
        detachTracking()
        element = null
        // Re-acquisition gets a FRESH search window — without this reset the
        // replacement is judged against the original deadline, which may
        // already be near/past expiry for a late detach (greptile #18056).
        deadline = Date.now() + timeoutMs
        setState({ rect: null, status: 'searching', targetId })
        startPolling()
        return
      }
      setState({ rect: element.getBoundingClientRect(), status: 'found', targetId })
    }

    const scheduleMeasure = (): void => {
      if (cancelled || frameHandle !== null) return
      if (typeof requestAnimationFrame === 'function') {
        frameHandle = requestAnimationFrame(measure)
      } else {
        measure()
      }
    }

    const detachTracking = (): void => {
      window.removeEventListener('resize', scheduleMeasure)
      window.removeEventListener('scroll', scheduleMeasure, true)
      resizeObserver?.disconnect()
      resizeObserver = null
    }

    const attach = (found: Element): void => {
      element = found
      setState({ rect: found.getBoundingClientRect(), status: 'found', targetId })
      window.addEventListener('resize', scheduleMeasure)
      window.addEventListener('scroll', scheduleMeasure, true)
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(scheduleMeasure)
        resizeObserver.observe(found)
      }
    }

    const startPolling = (): void => {
      pollTimer = setInterval(() => {
        if (cancelled) return
        const found = find()
        if (found) {
          if (pollTimer) clearInterval(pollTimer)
          pollTimer = null
          attach(found)
        } else if (Date.now() > deadline) {
          if (pollTimer) clearInterval(pollTimer)
          pollTimer = null
          setState({ rect: null, status: 'missing', targetId })
        }
      }, pollIntervalMs)
    }

    const immediate = find()
    if (immediate) {
      attach(immediate)
    } else {
      startPolling()
    }

    return () => {
      cancelled = true
      if (pollTimer) clearInterval(pollTimer)
      if (frameHandle !== null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(frameHandle)
      }
      detachTracking()
    }
  }, [targetId, pollIntervalMs, timeoutMs])

  return state
}
